import React, { useState } from 'react';
import { Form } from 'react-final-form';
import classnames from 'classnames';
import { FormApi } from 'final-form';

import { BottomSubmitBlock } from 'shared/ui/Button';
import { SkillTaxonomyDto } from '@eva/types/dto/skills';
import type { SkillTaxonomySection, SkillTaxonomyTreeDto } from '@eva/types/dto';
import PageSpinner from 'shared/ui/Spinner/PageSpinner';

import { SkillTaxonomyTreeItemForm } from 'entities/skillTaxonomy/SkillTaxonomyTreeItemForm';
import { isSelectedSkillsExceeded } from 'entities/skillTaxonomy/SkillTaxonomyTreeItemForm/functions';
import { SkillTaxonomyTreeButtons } from 'entities/skillTaxonomy/ui';
import { useSkillTaxonomyTree } from 'entities/skillTaxonomy/hooks/useSkillTaxonomyTree';

import type { UseQueryResult } from 'react-query/types/react/types';

import { onExpandGroup, mapFormDataToBody, convertError } from '../../functions';

import { Note } from './Note';
import { InfoAlert } from './InfoAlert';
import { EditNoteButton } from './Buttons/EditNoteButton';
import { TopFormButtons } from './Buttons/TopFormButtons';

type SkillTaxonomyTreeItemEditableType = {
  note: string;
  skills: SkillTaxonomySection['skills'];
  isEditAllowed: boolean;
  highlightSkillLabelMemoized?: (skill: any, isEditMode?: boolean) => void;
  hooks: {
    useFetchSkillTaxonomyTree: () => UseQueryResult<SkillTaxonomyTreeDto>;
    useUpdateSkillTaxonomyNote: any;
    useUpdateEntityTree: any;
  };
  name: SkillTaxonomyDto['name'];
  skillProficiencies: SkillTaxonomyDto['proficiencies'];
  maxNumberOfSelectedSkills: number;
  defaultExpandedSkills?: number[];
};

type SkillTaxonomyFormType = {
  skillId: number;
  skillProficiencyValue: number | null;
}[];

export const SkillTaxonomyTreeItemEditable: React.FC<SkillTaxonomyTreeItemEditableType> = ({
  skills,
  note,
  isEditAllowed,
  highlightSkillLabelMemoized,
  hooks,
  maxNumberOfSelectedSkills,
  skillProficiencies,
  name,
  defaultExpandedSkills,
}) => {
  const [isUpdatingEntity, setIsUpdatingEntity] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isEditSkill, setIsEditSkill] = useState(false);
  const [isEditNote, setIsEditNote] = useState(false);

  const { error, isLoading, onExpandTree, skillTaxonomyTree, setSkillTaxonomyTree } = useSkillTaxonomyTree({
    useFetchSkillTaxonomyTree: hooks.useFetchSkillTaxonomyTree,
    defaultExpandedSkills,
  });

  const updateEntityTree = hooks.useUpdateEntityTree();

  const updateSkillTaxonomyNote = hooks.useUpdateSkillTaxonomyNote();

  const updateEntitySubmit = async (data: any) => {
    const skillsData: SkillTaxonomyFormType = mapFormDataToBody(data);
    setIsUpdatingEntity(true);
    try {
      await updateEntityTree({
        skills: skillsData,
      });
      toggleSkillEdit();
    } catch (error) {
      setSubmitError(convertError(error));
    } finally {
      setIsUpdatingEntity(false);
    }
  };

  const toggleSkillEdit = () => {
    setSubmitError('');
    setIsEditSkill(!isEditSkill);
  };

  const onCancelForm = (form: FormApi<any, Partial<any>>) => {
    form.reset();
    toggleSkillEdit();
  };

  const toggleNoteEdit = () => {
    setIsEditNote(!isEditNote);
  };

  const getDisableButtonStatus = (skillsData: any[]) =>
    isUpdatingEntity || isSelectedSkillsExceeded(skillsData, maxNumberOfSelectedSkills);

  const renderTopButtons = (values: any, form: FormApi<any, Partial<any>>) => (
    <div className="pull-right">
      <SkillTaxonomyTreeButtons onExpandTree={onExpandTree} />
      {isEditAllowed && !isEditNote && <EditNoteButton toggleNoteEdit={toggleNoteEdit} />}
      {isEditAllowed && (
        <TopFormButtons
          isEditSkill={isEditSkill}
          toggleSkillEdit={toggleSkillEdit}
          getDisableButtonStatus={() => getDisableButtonStatus(values.skills)}
          isUpdatingEntity={isUpdatingEntity}
          onCancelForm={() => onCancelForm(form)}
        />
      )}
    </div>
  );

  if (isLoading) {
    return <PageSpinner />;
  }

  if (error) {
    return <h3 className="text-danger">{error}</h3>;
  }

  const formClassName = classnames({
    'edit-user': isEditSkill,
    section: !isEditSkill,
  });

  return (
    <div>
      <div className="taxonomy-wrapper">
        <Form
          onSubmit={updateEntitySubmit}
          initialValues={{
            skills,
          }}
          render={({ handleSubmit, values, form }) => (
            <form onSubmit={handleSubmit} className={formClassName}>
              {renderTopButtons(values, form)}
              <h3 className="text-primary">{name}</h3>
              <Note
                note={note}
                isEditNote={isEditNote}
                submitting={isUpdatingEntity}
                toggleNoteEdit={toggleNoteEdit}
                onSubmit={updateSkillTaxonomyNote}
                isEditAllowed={isEditAllowed}
              />
              {skillTaxonomyTree && (
                <SkillTaxonomyTreeItemForm
                  isDisabled={!isEditSkill || isUpdatingEntity}
                  isUpdatingEntity={isUpdatingEntity}
                  isEditSkill={isEditSkill}
                  skillTaxonomyTree={skillTaxonomyTree}
                  skillProficiencies={skillProficiencies}
                  highlightSkillLabelMemoized={
                    highlightSkillLabelMemoized
                      ? (skill) => highlightSkillLabelMemoized?.(skill, isEditSkill)
                      : highlightSkillLabelMemoized
                  }
                  onExpandGroup={onExpandGroup({
                    skillTaxonomyTree,
                    setSkillTaxonomyTree,
                  })}
                />
              )}
              {isEditSkill && (
                <>
                  {isSelectedSkillsExceeded(values.skills, maxNumberOfSelectedSkills) && (
                    <InfoAlert maxNumberOfSelectedSkills={maxNumberOfSelectedSkills} />
                  )}
                  {submitError && <h3 className="text-center text-danger">{submitError}</h3>}
                  <BottomSubmitBlock
                    onCancelForm={() => onCancelForm(form)}
                    isDisabledSubmit={getDisableButtonStatus(values.skills)}
                    isDisabledCancel={isUpdatingEntity}
                    cancelText={translate('Cancel')}
                    submitText={translate(isUpdatingEntity ? 'Submitting' : 'Submit')}
                  />
                </>
              )}
            </form>
          )}
        />
      </div>
    </div>
  );
};
